import { storage } from '@/firebaseConfig/firebaseConfig';
import * as Sentry from '@sentry/capacitor';
// hooks/useOrganizationLogo.ts
import { useState, useEffect } from 'react';

import { isDevelopment } from '@/utils';

type LogoType = 'profile' | 'client' | 'header';

interface UseOrganizationLogoOptions {
  logoFileName: string | undefined;
  logoType?: LogoType;
  asBase64?: boolean;
}

const LOGO_PATHS: Record<LogoType, string> = {
  profile: 'organisation_logos/',
  client: 'PDF_Download_Organisation_Logos/',
  header: 'PDF_Download_Header_Logos/',
};

export const useOrganizationLogo = (
  logoFileNameOrOptions: string | undefined | UseOrganizationLogoOptions,
  logoType: LogoType = 'profile',
) => {
  const options: UseOrganizationLogoOptions =
    typeof logoFileNameOrOptions === 'string' || logoFileNameOrOptions === undefined
      ? { logoFileName: logoFileNameOrOptions, logoType }
      : logoFileNameOrOptions;

  const { logoFileName, logoType: type = 'profile', asBase64 = false } = options;

  const [logoURL, setLogoURL] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!logoFileName) {
      setLoading(false);
      return;
    }

    const fetchLogo = async () => {
      try {
        setLoading(true);

        const logoPath = `${LOGO_PATHS[type]}${logoFileName}.png`;
        if (isDevelopment) {
          console.log(`Fetching logo from: ${logoPath}`);
        }

        const logoRef = storage.ref(logoPath);

        if (asBase64) {
          // Fetch the download URL and retrieve the blob via fetch
          const url = await logoRef.getDownloadURL();
          if (isDevelopment) {
            console.log(`Logo download URL retrieved for ${type}: ${url}`);
          }
          const response = await fetch(url);
          const blob = await response.blob();
          if (isDevelopment) {
            console.log(`Blob retrieved for ${type} logo, size: ${blob.size} bytes`);
          }

          // Converteer blob naar base64
          const reader = new FileReader();

          reader.onloadend = () => {
            if (isDevelopment) {
              console.log(`${type} logo converted to base64`);
            }
            setLogoURL(reader.result as string);
            setError(null);
            setLoading(false);
          };

          reader.onerror = () => {
            Sentry.captureException(error);
            const err = new Error('Failed to convert image to base64');
            console.error(err);
            setError(err);
            setLoading(false);
          };

          reader.readAsDataURL(blob);
        } else {
          // Voor normale gebruik (niet-PDF) - gebruik compat API
          const url = await logoRef.getDownloadURL();
          if (isDevelopment) {
            console.log(`Logo URL retrieved: ${url}`);
          }
          setLogoURL(url);
          setError(null);
          setLoading(false);
        }
      } catch (err) {
        Sentry.captureException(error);
        console.error(`Error loading ${type} logo:`, err);
        setError(err as Error);
        setLoading(false);
      }
    };

    fetchLogo();
  }, [logoFileName, type, asBase64]);

  return { logoURL, loading, error };
};
